import { useState } from "react";
import { toast } from "sonner";
import { FileText, FileImage, FileSpreadsheet, File as FileIcon, Download, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/positions";
import { useLang, errorText } from "@/lib/i18n";

export const fmtSize = (b, lang = "fr") => {
  const [k, m] = lang === "en" ? ["KB", "MB"] : ["Ko", "Mo"];
  return b >= 1048576 ? `${(b / 1048576).toFixed(1)} ${m}` : `${Math.max(1, Math.round(b / 1024))} ${k}`;
};
export const DocIcon = ({ type, className = "w-4 h-4" }) => {
  const Icon = type.startsWith("image/") ? FileImage : /sheet|excel|csv/.test(type) ? FileSpreadsheet : /pdf|word|text|rtf|opendocument/.test(type) ? FileText : FileIcon;
  return <Icon className={`${className} text-[#C5A059] shrink-0`} />;
};

export const useOpenDoc = () => {
  const { t } = useLang();
  const [busy, setBusy] = useState(null);
  const open = async (d) => {
    const win = window.open("", "_blank");
    setBusy(d.id);
    try {
      const r = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: d.content_type }));
      const inline = d.content_type.startsWith("image/") || d.content_type === "application/pdf" || d.content_type.startsWith("text/");
      if (inline && win) win.location.href = url;
      else {
        if (win) win.close();
        const a = Object.assign(document.createElement("a"), { href: url, download: d.filename });
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (win) win.close();
      toast.error(errorText(t, e));
    } finally {
      setBusy(null);
    }
  };
  return { open, busy };
};

export const DocRow = ({ d, onOpen, busy, children }) => {
  const { lang } = useLang();
  return (
  <li className="flex items-center gap-3 py-2.5 min-w-0" data-testid={`doc-${d.id}`}>
    <DocIcon type={d.content_type} />
    <button onClick={() => onOpen(d)} className="min-w-0 flex-1 text-left group" data-testid={`doc-open-${d.id}`}>
      <span className="block text-sm truncate group-hover:text-[#D9B872] underline-offset-4 group-hover:underline">{d.filename}</span>
      <span className="block text-[11px] text-neutral-500">{fmtSize(d.size, lang)}</span>
    </button>
    {children}
    <button onClick={() => onOpen(d)} className="press p-2 rounded-full border border-black/10 hover:border-[#C5A059]" aria-label="download" data-testid={`doc-download-${d.id}`}>
      {busy === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
    </button>
  </li>
  );
};

export const DocumentList = ({ eventId }) => {
  const { t } = useLang();
  const { data } = useFetch(`/events/${eventId}/documents`);
  const { open, busy } = useOpenDoc();
  if (!data || !data.length) return null;
  return (
    <div className="mt-4" data-testid={`event-docs-${eventId}`}>
      <div className="eyebrow mb-1">{t("doc.title")}</div>
      <ul className="divide-y divide-black/[0.05]">{data.map((d) => <DocRow key={d.id} d={d} onOpen={open} busy={busy} />)}</ul>
    </div>
  );
};
